import type { Venue } from '@Types/venue';
import { cn } from '@Utils/cn';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  TE_OPTIONAL_LABELS,
  TE_REQUIRED_LABELS,
  evaluateTicketedEventsForVenue,
} from './features/ticketedEvents';
import MatrixCell from './MatrixCell';
import { FeatureOnOffBadge } from './StatusBadge';

const STICKY_LABEL_CELL =
  'sticky left-0 z-10 bg-white border-r border-zinc-200';

const HEADER_CELL = 'px-3 py-2 text-xs font-medium text-zinc-600 text-center';

const SECTION_BORDER = 'border-l border-zinc-200';
const OPTIONAL_SECTION_BORDER = 'border-l-2 border-zinc-300';

const TOTAL_FEATURE_COLS =
  1 + TE_REQUIRED_LABELS.length + TE_OPTIONAL_LABELS.length;

export default function TicketedEventsMatrix({ venues }: { venues: Venue[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th
              rowSpan={3}
              className={cn(
                STICKY_LABEL_CELL,
                'border-b border-zinc-200 px-3 py-2 text-left text-xs font-medium text-zinc-600',
              )}
            >
              Venue
            </th>
            <th
              colSpan={TOTAL_FEATURE_COLS}
              className="border-b border-l border-zinc-200 bg-zinc-50 px-3 py-2 text-center text-xs font-semibold text-zinc-700"
            >
              Ticketed Events
            </th>
          </tr>
          <tr>
            <th
              rowSpan={2}
              className={cn(HEADER_CELL, 'border-b border-l border-zinc-200')}
            >
              Status
            </th>
            <th
              colSpan={TE_REQUIRED_LABELS.length}
              className={cn(
                HEADER_CELL,
                'border-b border-zinc-200',
                SECTION_BORDER,
              )}
            >
              Required
            </th>
            <th
              colSpan={TE_OPTIONAL_LABELS.length}
              className={cn(
                HEADER_CELL,
                'border-b border-zinc-200',
                OPTIONAL_SECTION_BORDER,
              )}
            >
              Optional
            </th>
          </tr>
          <tr>
            {TE_REQUIRED_LABELS.map((label, i) => (
              <th
                key={label}
                className={cn(
                  HEADER_CELL,
                  'border-b border-zinc-200',
                  i === 0 && SECTION_BORDER,
                )}
              >
                {label}
              </th>
            ))}
            {TE_OPTIONAL_LABELS.map((label, i) => (
              <th
                key={label}
                className={cn(
                  HEADER_CELL,
                  'border-b border-zinc-200',
                  i === 0 && OPTIONAL_SECTION_BORDER,
                )}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {venues.map((venue) => {
            const ev = evaluateTicketedEventsForVenue(venue);
            return (
              <tr key={venue.id} className="hover:bg-zinc-50">
                <td
                  className={cn(
                    STICKY_LABEL_CELL,
                    'border-b border-zinc-100 px-3 py-2 font-medium text-zinc-900',
                  )}
                >
                  <Link
                    to={`/venues/${venue.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:underline"
                  >
                    {venue.name}
                    <ExternalLink className="size-3.5 opacity-60" />
                  </Link>
                </td>
                <td className="border-b border-l border-zinc-200 px-3 py-2 text-center">
                  <FeatureOnOffBadge on={ev.on} />
                </td>
                {ev.required.map((cell, i) => (
                  <td
                    key={`req-${i}`}
                    className={cn(
                      'border-b border-zinc-100 px-3 py-2 text-center',
                      i === 0 && SECTION_BORDER,
                    )}
                  >
                    <MatrixCell state={cell} />
                  </td>
                ))}
                {ev.optional.map((cell, i) => (
                  <td
                    key={`opt-${i}`}
                    className={cn(
                      'border-b border-zinc-100 px-3 py-2 text-center',
                      i === 0 && OPTIONAL_SECTION_BORDER,
                    )}
                  >
                    <MatrixCell state={cell} />
                  </td>
                ))}
              </tr>
            );
          })}
          {venues.length === 0 && (
            <tr>
              <td
                colSpan={1 + TOTAL_FEATURE_COLS}
                className="px-3 py-8 text-center text-sm text-zinc-500"
              >
                No venues to display.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
